import { useEffect, useRef } from "react";

import {
  resolveRefreshPolicy,
  type TabVisibility,
  type UserEngagement
} from "@/lib/refresh-policy";
import { getMarketState } from "@/lib/market-status";
import { useRefreshBus } from "@/store/refresh-bus-store";

interface SchedulerInputs {
  visibility: TabVisibility;
  engagement: UserEngagement;
}

/**
 * Fire Tier-1 refresh requests based on the policy table in
 * `docs/REFRESH-POLICY.md`:
 *
 *   • 15 min interval while visible + active + market-open.
 *   • Immediate `requestT1Refresh()` on focus-regained (the
 *     visibility transition from hidden → visible).
 *   • Immediate `requestT1Refresh()` on the `online` event (cheap
 *     high-value refresh: users resume after an iPhone Wi-Fi hop).
 *
 * Everything bumps the shared refresh-bus token. Data hooks and the
 * hero FreshnessDot listen for changes there — this scheduler has no
 * opinion about HOW the refresh happens.
 *
 * Mount once per authenticated shell. Multiple mounts would multiply
 * the interval firings; the hook isn't guarded against that.
 */
export function useTierOneScheduler({ visibility, engagement }: SchedulerInputs) {
  const requestT1Refresh = useRefreshBus((s) => s.requestT1Refresh);

  // Keep the latest values in a ref so event listeners (focus, online)
  // don't need their own effect-remount dance to read current state.
  const inputsRef = useRef({ visibility, engagement });
  useEffect(() => {
    inputsRef.current = { visibility, engagement };
  }, [visibility, engagement]);

  // Interval path — 15 min polling while the policy allows.
  useEffect(() => {
    const marketState = getMarketState();
    const policy = resolveRefreshPolicy({
      tier: "T1_LIVE",
      visibility,
      marketState,
      engagement
    });
    if (!policy.enabled || policy.intervalMs === null) return;
    // Recursive setTimeout rather than setInterval so wake-from-sleep
    // doesn't accumulate a backlog of missed ticks — we fire exactly
    // once per intervalMs regardless of how long the laptop was out.
    let cancelled = false;
    let timeoutId: number | null = null;
    const tick = () => {
      if (cancelled) return;
      // Re-evaluate policy on each tick: market may have transitioned
      // open → closed during the interval, which changes the tier.
      const cur = inputsRef.current;
      const nowPolicy = resolveRefreshPolicy({
        tier: "T1_LIVE",
        visibility: cur.visibility,
        marketState: getMarketState(),
        engagement: cur.engagement
      });
      if (nowPolicy.enabled && nowPolicy.intervalMs !== null) {
        requestT1Refresh();
      }
      timeoutId = window.setTimeout(tick, policy.intervalMs ?? 15 * 60_000);
    };
    timeoutId = window.setTimeout(tick, policy.intervalMs);
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [visibility, engagement, requestT1Refresh]);

  // Focus-regained path — fire an immediate refresh when the tab
  // returns to visibility (reads current inputs from ref).
  useEffect(() => {
    if (visibility !== "visible") return;
    // Only fire if this effect-instance is starting *because of* a
    // hidden→visible transition. React's effect re-runs on the
    // visibility change itself, so presence-of-effect is the signal.
    // Skip the initial mount using a ref so we don't double-fire a
    // refresh that the interval is about to handle anyway.
    if (!justMountedRef.current) {
      const policy = resolveRefreshPolicy({
        tier: "T1_LIVE",
        visibility,
        marketState: getMarketState(),
        engagement
      });
      if (policy.enabled) requestT1Refresh();
    }
    justMountedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  const justMountedRef = useRef(true);

  // Network regained — `online` event. Refresh once regardless of
  // interval state; users resume from Wi-Fi hops and want a fresh
  // snapshot without pulling-to-refresh.
  useEffect(() => {
    const onOnline = () => {
      const cur = inputsRef.current;
      const policy = resolveRefreshPolicy({
        tier: "T1_LIVE",
        visibility: cur.visibility,
        marketState: getMarketState(),
        engagement: cur.engagement
      });
      if (policy.enabled) requestT1Refresh();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [requestT1Refresh]);
}
